import { getTrialEndDate } from '@magicschool/business-logic/trials';
import { Button, Divider, Icon, Popover, Text } from '@magicschool/spellbook';
import Link from 'next/link';
import { FormattedMessage } from 'react-intl';
import { useAuth } from '@/hooks/useAuth';
import { analyticsTrack } from '@/util/amplitude/analyticsClient';

type UpsellPopoverProps = {
  children: React.ReactNode;
  location: 'leftNav' | 'banner';
  isAdminProfession?: boolean;
};

function UpsellReasonItem({ index }: { index: number }) {
  return (
    <li className="flex gap-2">
      <Icon name="check" color="default" />
      <Text variant="label-1" color="default">
        <FormattedMessage id={`popover.trial-cta.reasons.${index}`} />
      </Text>
    </li>
  );
}

const TEACHER_REASON_INDICES = [1, 2, 3, 4, 5, 6, 7, 8];
const ADMIN_REASON_INDICES = [1, 4, 9, 10, 11, 12, 13, 14, 15, 16, 17, 8];

export default function UpsellPopover({ children, location, isAdminProfession = false }: UpsellPopoverProps) {
  const { planId, userTrials } = useAuth();
  const reasonIndices = isAdminProfession ? ADMIN_REASON_INDICES : TEACHER_REASON_INDICES;

  return (
    <Popover>
      <Popover.Trigger
        onClick={() => {
          analyticsTrack('Navigation:Action', {
            href: window.location.href,
            location,
            value: 'review_plus_plan',
          });
        }}
      >
        {children}
      </Popover.Trigger>
      <Popover.Content className="px-4 py-6 max-w-[calc(100vw-2rem)] sm:max-w-[388px]">
        <Text element="h1" variant="heading-3" className="mb-1">
          <FormattedMessage id="popover.trial-cta.header" />
        </Text>
        <Text variant="body-2">
          <FormattedMessage id="popover.trial-cta.header-info" values={{ endDate: getTrialEndDate(planId, userTrials) }} />
        </Text>

        <Divider className="my-3" />

        <Text element="h2" variant="label-2" color="default-soft">
          <FormattedMessage id={isAdminProfession ? 'popover.trial-cta.subheader.admin' : 'popover.trial-cta.subheader'} />
        </Text>
        <ol className="flex flex-col my-2 gap-2">
          {reasonIndices.map((index) => (
            <UpsellReasonItem key={index} index={index} />
          ))}
        </ol>
        <Button
          variant="ghost"
          onClick={() => {
            analyticsTrack('Navigation:Action', {
              href: window.location.href,
              location,
              value: 'review_subscription',
            });
          }}
        >
          <Link href="/subscription" target="_blank">
            <FormattedMessage id={isAdminProfession ? 'popover.trial-cta.see-more.admin' : 'popover.trial-cta.see-more'} />
          </Link>
          <Button.TrailingIcon name="external-link" />
        </Button>

        <Divider className="my-3" />

        <Text variant="body-2" color="default" className="whitespace-pre-line">
          <FormattedMessage id="popover.trial-cta.footer.line1" />
          <br />
          <FormattedMessage id="popover.trial-cta.footer.line2" />
        </Text>
      </Popover.Content>
    </Popover>
  );
}
